import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { Paper, Stack, Divider, Chip } from "@mui/material";
import { convertTimeFormat } from "../../utils/timeFormatter";
import Dialog from "@mui/material/Dialog";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DOMPurify from 'dompurify';
import {CircularProgress} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";


const MobileMyNotifications = ({notifications, isLoading, markAsRead, markAllAsRead, unreadCount}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogData, setDialogData] = useState({
        sentBy: "",
        time: "",
        message: "",
        notificationId: null,
    });

    const handleCloseDialog = () => {
        setDialogData({});
        setOpenDialog(false);
    };

    const handleViewMessageClick = ({ message, sentBy, time, notificationId, isRead }) => {
        setDialogData({ sentBy, message, time, notificationId });
        setOpenDialog(true);
        // Mark as read when viewing
        if (!isRead && markAsRead && notificationId) {
            markAsRead(notificationId);
        }
    };

    const handleOrderNumberClick = (id, serviceName) => {
        navigate(`/order-details/${id}`, {
            state: { groupName: serviceName || t('notif_orderDetails') }
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={80} />
            </Box>
        );
    }

    if (!notifications || notifications.length === 0) {
        return (
            <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                    {t('notif_noNotifications')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('notif_noNotificationsDesc')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                "& .table-header": {
                    backgroundColor: "#DFE7F2",
                    color: "#BF3C4E",
                    border: "1px solid white",
                },
                "& .MuiDataGrid-columnHeaders": {
                    overflowX: { xs: "scroll", md: "hidden" },
                },
                "& .MuiDataGrid-footerContainer": {
                    direction: "ltr",
                },
                "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#d9e0eb",
                },
                padding: 2
            }}
        >

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                sx={{
                    "& .MuiDialog-paper": {
                        border: "2px solid #BF3C4E", // Set the border color here
                        padding: "20px",
                        borderRadius: "10px",
                    },
                }}
            >
                <Stack gap={3} sx={{ marginBottom: 5 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 2,
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold" }}>{t('notif_sentBy')} :</Typography>
                        <Typography>{dialogData.sentBy}</Typography>
                    </Box>
                    <Divider sx={{ width: "100%" }} />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 2,
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold" }}>{t('notif_sendTime')} :</Typography>
                        <Typography>{dialogData.time}</Typography>
                    </Box>
                    <Divider sx={{ width: "100%" }} />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 1,
                            alignItems:{xs: "start", md: "center"}
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold" }}>{t('notif_messageBody')}:</Typography>
                        <Typography dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dialogData.message) }}></Typography>
                    </Box>
                </Stack>
                <Button
                    type="sumbit"
                    sx={{ width: "30%", alignSelf: "center" }}
                    variant="contained"
                    onClick={handleCloseDialog}
                >
                    <Typography>{t('notif_close')}</Typography>
                </Button>
            </Dialog>
            <Stack gap={2}>
                {
                    notifications?.map(notif=> {
                        return (
                            <Paper 
                                key={notif.id} 
                                sx={{
                                    p:2,
                                    backgroundColor: !notif.isRead ? 'rgba(191, 60, 78, 0.08)' : 'inherit',
                                    borderRight: !notif.isRead ? '4px solid #BF3C4E' : 'none',
                                }} 
                                elevation={5}
                            >
                                <Stack gap={2}>
                                    {/* Unread indicator */}
                                    {!notif.isRead && (
                                        <Box display="flex" justifyContent="flex-start">
                                            <Chip 
                                                size="small" 
                                                label={t('notif_new')} 
                                                color="primary" 
                                                icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
                                            />
                                        </Box>
                                    )}
                                    <Box display="flex" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_sendTime')}:</Typography>
                                        <Typography sx={{direction: "ltr", textAlign: "end"}}>{convertTimeFormat(notif.timestamp)}</Typography>
                                    </Box>
                                    <Box display="flex" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_type')}:</Typography>
                                        <Typography>{notif.type.name}</Typography>
                                    </Box>
                                    <Box display="flex" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_sentTo')}:</Typography>
                                        <Typography>{notif.sentToName}</Typography>
                                    </Box>
                                    <Box display="flex" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_sendInfo')}:</Typography>
                                        <Typography title={notif.sentTo} sx={{ whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",}}>{notif.sentTo}</Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_orderNumber')}:</Typography>
                                        {notif.subscriberServiceFullNumber ? (
                                            <Typography
                                                sx={{
                                                    cursor: notif.subscriberServiceId ? "pointer" : "default",
                                                    textDecoration: notif.subscriberServiceId ? "underline" : "none"
                                                }}
                                                onClick={notif.subscriberServiceId ? () => handleOrderNumberClick(notif.subscriberServiceId, notif.serviceName) : undefined}
                                            >
                                                {notif.subscriberServiceFullNumber}}
                                            </Typography>
                                        ) : (
                                            <Typography>-</Typography>
                                        )}
                                        {notif.subscriberServiceFullNumber && notif.subscriberServiceId && (
                                            <IconButton onClick={() => handleOrderNumberClick(notif.subscriberServiceId, notif.serviceName)}>
                                                <OpenInNewIcon color="primary" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography sx={{fontWeight: "bold"}}>{t('notif_message')}:</Typography>
                                        <IconButton onClick={()=> handleViewMessageClick({
                                            message: notif.message,
                                            sentBy: notif.sentByUser,
                                            time: convertTimeFormat(notif.timestamp),
                                            notificationId: notif.id,
                                            isRead: notif.isRead,
                                        })}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Box>

                                </Stack>
                            </Paper>
                        )
                    })
                }
            </Stack>
        </Box>
    );
};

export default MobileMyNotifications;
